import { useEffect, useMemo, useState } from "react";
import { Modal, Button, FormField, DraftNotice } from "../../../components/common";
import useFormDraft from "../../../hooks/useFormDraft";
import { DEFAULT_SECTIONS } from "../../../services/core";

const SECTION_LABELS = {
  summary: "Overall comments",
  strengths: "Strengths",
  improvement_areas: "Areas to improve",
  development_plan: "Development plan",
  key_achievements: "Key achievements",
  challenges: "Challenges",
  development_areas: "Development areas",
  comments: "Overall comments",
};
const labelOf = (key) => SECTION_LABELS[key] || key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const TYPE_LABEL = { GOAL: "Goal (% achieved)", KRA: "KRA (actual)", KPI: "KPI (actual)" };

function initialForm(record, sectionKeys) {
  const manager = record?.manager_assessment || {};
  const self = record?.self_assessment || {};
  const byId = (list) => Object.fromEntries((list || []).map((x) => [String(x.item_id), x]));
  const m = byId(manager.items);
  const s = byId(self.items);
  const items = (record?.items || []).map((it) => {
    const id = String(it.performance_item_id);
    const source = m[id] || s[id];
    return {
      item_id: it.performance_item_id,
      type: it.item_type,
      title: it.item_title,
      unit: it.measurement_unit,
      target: it.target,
      weight: it.weight,
      achievement: source?.achievement === undefined || source?.achievement === null ? "" : String(Number(source.achievement)),
      comment: m[id]?.comment || "",
    };
  });
  const sections = Object.fromEntries(sectionKeys.map((k) => [k, (manager.sections || {})[k] || ""]));
  return { items, sections, reason: "" };
}

export default function CorrectionModal({ open, data, busy, onClose, onSubmit }) {
  const sectionKeys = useMemo(
    () => data?.cycle?.workflow?.assessment_sections || DEFAULT_SECTIONS,
    [data]
  );
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const f = initialForm(data?.record, sectionKeys);
    setForm(f);
    setInitial(JSON.stringify({ items: f.items, sections: f.sections }));
    setError("");
  }, [open, data, sectionKeys]);

  // [NEW] Keep an unfinished correction (scores, comments, reason) for this appraisal revision.
  const recordKey = `${data?.record?.performance_record_id || ""}:${data?.record?.current_result_revision_id || ""}`;
  const draft = useFormDraft(`correction:${recordKey}`, form, setForm, { active: Boolean(open && form) });

  if (!open || !form) return null;

  const changed = JSON.stringify({ items: form.items, sections: form.sections }) !== initial;
  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));

  const submit = () => {
    if (!form.reason.trim()) { setError("Write why the result is being corrected."); return; }
    const missing = form.items.filter((x) => x.achievement === "" || Number.isNaN(Number(x.achievement)) || Number(x.achievement) < 0);
    if (missing.length) { setError(`Enter a valid number (0 or more) for: ${missing.map((x) => x.title).join(", ")}.`); return; }
    const emptySection = sectionKeys.find((k) => !String(form.sections[k] || "").trim());
    if (emptySection) { setError(`"${labelOf(emptySection)}" can't be empty.`); return; }
    setError("");
    // runCorrection resolves to true when the correction was saved
    Promise.resolve(onSubmit({
      reason: form.reason.trim(),
      correction: {
        sections: Object.fromEntries(sectionKeys.map((k) => [k, String(form.sections[k]).trim()])),
        items: form.items.map((x) => ({ item_id: x.item_id, achievement: Number(x.achievement), comment: x.comment.trim() })),
      },
    })).then((ok) => { if (ok) draft.clear(); });
  };

  return (
    <Modal
      open={open}
      size="lg"
      title="Correct finalized appraisal"
      subtitle="Change the numbers or comments below. The result is re-scored into a new revision, which you release again. Recorded in the audit trail."
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={submit} loading={busy} disabled={!changed}>
            {changed ? "Save correction" : "Change something to save"}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <DraftNotice draft={draft} />
        {form.items.length > 0 && (
          <div className="table-wrap">
            <table className="data-table is-dense">
              <thead>
                <tr><th>Item</th><th>Type</th><th>Target</th><th>Value</th><th>Comment</th></tr>
              </thead>
              <tbody>
                {form.items.map((x, i) => (
                  <tr key={x.item_id}>
                    <td>{x.title}<br /><small className="muted">Weight {Number(x.weight)}%</small></td>
                    <td>{TYPE_LABEL[x.type] || x.type}</td>
                    <td>{Number(x.target)} {x.unit}</td>
                    <td>
                      <input
                        className="form-control"
                        type="number"
                        min="0"
                        step="any"
                        aria-label={`Value for ${x.title}`}
                        value={x.achievement}
                        onChange={(e) => setItem(i, { achievement: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        aria-label={`Comment for ${x.title}`}
                        value={x.comment}
                        onChange={(e) => setItem(i, { comment: e.target.value })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {sectionKeys.map((k) => (
          <FormField key={k} label={labelOf(k)} required>
            <textarea rows={2} value={form.sections[k]} onChange={(e) => setForm((f) => ({ ...f, sections: { ...f.sections, [k]: e.target.value } }))} />
          </FormField>
        ))}
        <FormField label="Reason for the correction" required error={error || undefined}>
          <textarea rows={2} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
        </FormField>
        <p className="form-hint">
          Sending the review back to the manager or employee to redo is not available yet (it needs a backend change).
          Use this form to correct the numbers directly.
        </p>
      </div>
    </Modal>
  );
}
